import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import { formatReviewHtml } from "../../utilities/formatReviewHtml";
import { useDraggableCard } from "../../hooks/useDraggableCard";

export interface SuggestionChange {
  id: string;
  type: "placeholder_fill" | "content_addition" | "sentence_rewrite";
  description: string;
  suggestion_addressed?: string;
  content: string;
  placeholder?: string;
  anchor_text?: string;
  original_text?: string;
  position?: "after" | "end_of_section" | "end_of_document";
  section_heading?: string;
}

// The officer's decision for a single proposed change, reported to the parent
// so it can be persisted to the suggestions_review audit.
export interface ReviewDecision {
  type: string;
  decision: "accepted" | "discarded" | "skipped" | "not_reviewed";
  suggestion_addressed?: string;
  description?: string;
  original_text?: string;
  anchor_text?: string;
  placeholder?: string;
}

interface SuggestionsReviewProps {
  changes: SuggestionChange[];
  editor: any;
  onComplete: () => void;
  // Called when the user picks "Discard All" — the editor snapshot has been
  // restored, so the parent should clean up the source recording too.
  onCancel: () => void;
  onSaveEditor?: () => void;
  // Reports the officer's per-change accept/discard decisions once the review
  // ends (completed or discarded), for the suggestions_review audit.
  onReviewDecisions?: (decisions: ReviewDecision[]) => void;
  // Lifted lock — toggling this flips the canonical editable check in
  // TiptapEditor so the editor stays read-only the entire time the review
  // is active. Replaces direct editor.setEditable(false) calls.
  setIsReviewingChanges?: (value: boolean) => void;
}

export default function SuggestionsReview({
  changes,
  editor,
  onComplete,
  onCancel,
  onSaveEditor,
  onReviewDecisions,
  setIsReviewingChanges,
}: SuggestionsReviewProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [insertedRange, setInsertedRange] = useState<{
    from: number;
    to: number;
  } | null>(null);
  // Drag-by-header behavior shared with the form-field review card.
  const drag = useDraggableCard();
  const { position: dialogPosition, setPosition: setDialogPosition, handleDragStart } = drag;
  const snapshotRef = useRef<string>("");
  const scrollContainerRef = useRef<Element | null>(null);
  const isActiveRef = useRef(true);
  const dialogRef = drag.cardRef;

  // Save editor snapshot on mount and engage the lifted editor lock. The
  // canonical editable effect in TiptapEditor watches isReviewingChanges and
  // keeps the editor read-only the whole time we're mounted, so we never call
  // editor.setEditable directly — that previously left brief windows where
  // typing slipped in between the toggles.
  useEffect(() => {
    if (editor) {
      snapshotRef.current = editor.getHTML();
      // Find the scrollable container (the overflow-y-auto parent of the editor)
      const editorDom = editor.view.dom as HTMLElement;
      let container = editorDom.parentElement;
      while (container && getComputedStyle(container).overflowY !== "auto" && getComputedStyle(container).overflowY !== "scroll") {
        container = container.parentElement;
      }
      scrollContainerRef.current = container;
    }
    isActiveRef.current = true;
    setIsReviewingChanges?.(true);
    return () => {
      isActiveRef.current = false;
      setIsReviewingChanges?.(false);
    };
  }, [editor, setIsReviewingChanges]);

  // No navigation guards — pending_suggestions_changes is persisted server-side
  // when Bedrock returns, so the user can safely refresh, navigate to another
  // case, or close the tab. The review walkthrough auto-opens again next time
  // they load this case.

  // Find text position in the editor document by searching block-level nodes
  const findTextPosition = useCallback(
    (searchText: string): number | null => {
      if (!editor) return null;
      const doc = editor.state.doc;
      let foundPos: number | null = null;

      // Search each top-level block for the anchor text
      doc.descendants((node: any, pos: number) => {
        if (foundPos !== null) return false;
        // Only search leaf block nodes (paragraphs, headings) — not the doc itself
        if (node.isBlock && node.isTextblock) {
          const blockText = node.textContent;
          const idx = blockText.indexOf(searchText);
          if (idx !== -1) {
            // pos is the position of the block node; +1 to enter it, +idx for the offset within text
            // But we need the actual document position accounting for inline nodes
            let charsSeen = 0;
            node.forEach((child: any, offset: number) => {
              if (foundPos !== null) return;
              if (child.isText) {
                const childText = child.text || "";
                if (charsSeen + childText.length > idx && charsSeen <= idx) {
                  foundPos = pos + 1 + offset + (idx - charsSeen);
                }
                charsSeen += childText.length;
              }
            });
            // Fallback: if we matched the block but couldn't pinpoint within inline nodes
            if (foundPos === null && idx >= 0) {
              foundPos = pos + 1 + idx;
            }
            return false;
          }
        }
        return true;
      });

      return foundPos;
    },
    [editor]
  );

  // Find the position right after the block node containing a given position
  const findBlockEnd = useCallback(
    (pos: number): number => {
      if (!editor) return pos;
      const resolved = editor.state.doc.resolve(pos);
      return resolved.after(resolved.depth);
    },
    [editor]
  );

  // Scroll to a position and position the dialog next to it
  const scrollAndPositionDialog = useCallback(
    (pos: number) => {
      if (!editor) return;
      try {
        const domAtPos = editor.view.domAtPos(pos);
        if (domAtPos?.node) {
          const element =
            domAtPos.node instanceof Element
              ? domAtPos.node
              : domAtPos.node.parentElement;
          if (element) {
            // Scroll ONLY the editor's scroll container — not via
            // element.scrollIntoView, which walks every scrollable ancestor
            // (including overflow-hidden ones, which it can still scroll
            // programmatically) and ends up pushing the whole layout around
            // with no way for the user to scroll it back.
            const container = scrollContainerRef.current as HTMLElement | null;
            if (container) {
              const containerRect = container.getBoundingClientRect();
              const elementRect = element.getBoundingClientRect();
              const targetTop =
                container.scrollTop +
                (elementRect.top - containerRect.top) -
                (containerRect.height - elementRect.height) / 2;
              container.scrollTo({
                top: Math.max(0, targetTop),
                behavior: "smooth",
              });
            }
            // Wait for scroll to settle, then position dialog
            setTimeout(() => {
              const rect = element.getBoundingClientRect();
              const dialogEl = dialogRef.current;
              const dialogHeight = dialogEl?.offsetHeight || 250;
              const dialogWidth = dialogEl?.offsetWidth || 520;
              const viewportHeight = window.innerHeight;
              const viewportWidth = window.innerWidth;
              const toolbarHeight = 60; // toolbar + tabs height

              // Position below the highlighted text
              let top = rect.bottom + 12;
              // If not enough room below, position above
              if (top + dialogHeight > viewportHeight - 20) {
                top = rect.top - dialogHeight - 12;
              }
              // Clamp: never above toolbar, never below viewport
              top = Math.max(toolbarHeight + 10, Math.min(top, viewportHeight - dialogHeight - 20));

              // Center on the editor area (between sidebar left edge and sidebar right edge)
              // Find the editor content area for centering
              const editorEl = editor?.view?.dom?.closest?.('.max-w-4xl') || editor?.view?.dom?.parentElement;
              let left: number;
              if (editorEl) {
                const editorRect = editorEl.getBoundingClientRect();
                left = editorRect.left + editorRect.width / 2;
              } else {
                left = rect.left + rect.width / 2;
              }

              // Clamp so dialog stays fully within viewport
              const halfDialog = dialogWidth / 2;
              left = Math.max(halfDialog + 10, Math.min(left, viewportWidth - halfDialog - 10));

              setDialogPosition({ top, left });
            }, 100);
          }
        }
      } catch {
        setDialogPosition(null);
      }
    },
    [editor]
  );

  // Apply the current change to the editor with highlight. Returns true if applied successfully.
  // The editor stays read-only the entire time (TipTap commands run regardless
  // of the editable flag) — we never re-enable it here.
  const applyChange = useCallback(
    (change: SuggestionChange): boolean => {
      if (!editor) return false;

      if (change.type === "placeholder_fill" && change.placeholder) {
        const doc = editor.state.doc;
        let foundFrom = -1;
        let foundTo = -1;

        doc.descendants((node: any, pos: number) => {
          if (foundFrom >= 0) return false;
          if (node.isText) {
            const text = node.text || "";
            const idx = text.indexOf(change.placeholder!);
            if (idx !== -1) {
              foundFrom = pos + idx;
              foundTo = pos + idx + change.placeholder!.length;
              return false;
            }
          }
          return true;
        });

        if (foundFrom >= 0) {
          const beforeSize = editor.state.doc.content.size;
          editor
            .chain()
            .focus()
            .setTextSelection({ from: foundFrom, to: foundTo })
            .deleteSelection()
            .insertContent(change.content)
            .run();

          // Derive the highlight end from the ACTUAL document-size delta, not
          // change.content.length (string chars). The content can be large/HTML
          // (e.g. filling a placeholder with a full narrative); using the string
          // length makes `newTo` point past the end of the doc, and
          // addMark()/nodesBetween then crashes with
          // "Cannot read properties of undefined (reading 'nodeSize')".
          const afterSize = editor.state.doc.content.size;
          const insertedSize = afterSize - beforeSize + (foundTo - foundFrom);
          const newTo = Math.min(
            foundFrom + Math.max(0, insertedSize),
            editor.state.doc.content.size,
          );
          // Apply the highlight via a raw addMark transaction so we don't
          // create a non-empty selection. A non-empty selection would trigger
          // the BubbleMenu plugin's update → tippy.show(), which crashes
          // ("Cannot read properties of null (reading 'contains')") because
          // the DOM nodes it would anchor to were just replaced by
          // deleteSelection/insertContent above.
          const highlightMark = editor.schema.marks.highlight;
          if (highlightMark) {
            const tr = editor.state.tr.addMark(
              foundFrom,
              newTo,
              highlightMark.create({ color: "#bbf7d0" }),
            );
            editor.view.dispatch(tr);
          }

          setInsertedRange({ from: foundFrom, to: newTo });
          scrollAndPositionDialog(foundFrom);
        } else {
          // Placeholder not found in document — skip this change
          return false;
        }
      } else if (change.type === "content_addition") {
        let insertPos: number | null = null;
        const docSize = editor.state.doc.content.size;

        if (change.anchor_text) {
          const anchorPos = findTextPosition(change.anchor_text);
          if (anchorPos !== null) {
            insertPos = findBlockEnd(anchorPos);
          }
        }

        if (insertPos === null) {
          insertPos = docSize - 1;
        }

        // Clamp to valid document range
        insertPos = Math.min(insertPos, docSize);

        const beforeSize = editor.state.doc.content.size;
        editor
          .chain()
          .focus()
          .insertContentAt(insertPos, formatReviewHtml(change.content))
          .run();
        const afterSize = editor.state.doc.content.size;

        const insertedLength = afterSize - beforeSize;
        const newFrom = insertPos;
        const newTo = Math.min(
          insertPos + insertedLength,
          editor.state.doc.content.size,
        );

        // Highlight via raw addMark so we don't create a non-empty selection
        // that would crash TipTap's BubbleMenu plugin on the freshly replaced
        // DOM. See the comment in the placeholder_fill branch above.
        const highlightMark = editor.schema.marks.highlight;
        if (highlightMark) {
          const tr = editor.state.tr.addMark(
            newFrom,
            newTo,
            highlightMark.create({ color: "#bbf7d0" }),
          );
          editor.view.dispatch(tr);
        }

        setInsertedRange({ from: newFrom, to: newTo });
        scrollAndPositionDialog(newFrom);
      } else if (change.type === "sentence_rewrite" && change.original_text) {
        // Find the original text in the document
        const doc = editor.state.doc;
        let foundFrom = -1;
        let foundTo = -1;

        doc.descendants((node: any, pos: number) => {
          if (foundFrom >= 0) return false;
          if (node.isText) {
            const text = node.text || "";
            const idx = text.indexOf(change.original_text!);
            if (idx !== -1) {
              foundFrom = pos + idx;
              foundTo = pos + idx + change.original_text!.length;
              return false;
            }
          }
          return true;
        });

        if (foundFrom >= 0) {
          // Replace the original text with the new content
          const beforeSize = editor.state.doc.content.size;
          editor
            .chain()
            .focus()
            .setTextSelection({ from: foundFrom, to: foundTo })
            .deleteSelection()
            .insertContent(change.content)
            .run();

          // Derive the highlight end from the document-size delta, not the
          // string length — see the placeholder_fill branch above. Prevents the
          // out-of-bounds addMark crash on large/HTML replacements.
          const afterSize = editor.state.doc.content.size;
          const insertedSize = afterSize - beforeSize + (foundTo - foundFrom);
          const newTo = Math.min(
            foundFrom + Math.max(0, insertedSize),
            editor.state.doc.content.size,
          );

          // Highlight the rewritten text via raw addMark
          const highlightMark = editor.schema.marks.highlight;
          if (highlightMark) {
            const tr = editor.state.tr.addMark(
              foundFrom,
              newTo,
              highlightMark.create({ color: "#bbf7d0" }),
            );
            editor.view.dispatch(tr);
          }

          setInsertedRange({ from: foundFrom, to: newTo });
          scrollAndPositionDialog(foundFrom);
        } else {
          // Original text not found in document — skip this change
          return false;
        }
      }

      // Deselect after applying
      editor.commands.setTextSelection(editor.state.doc.content.size);
      return true;
    },
    [editor, findTextPosition, findBlockEnd, scrollAndPositionDialog]
  );

  // Apply the current change when currentIndex changes — auto-skip if it fails
  useEffect(() => {
    if (currentIndex < changes.length) {
      const success = applyChange(changes[currentIndex]);
      if (!success) {
        // Change couldn't be applied (e.g. placeholder not found) — record it as
        // skipped (a system outcome, not an officer decision) and skip to next.
        decisionsRef.current[currentIndex] = "skipped";
        if (currentIndex + 1 >= changes.length) {
          completeReview();
        } else {
          setCurrentIndex((i) => i + 1);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const restoreScroll = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // The officer's decision for each change, keyed by change index. Updated as
  // they accept/reject each step; reported to the parent when the review ends.
  const decisionsRef = useRef<Record<number, ReviewDecision["decision"]>>({});

  const buildDecisions = useCallback(
    (allDiscarded = false): ReviewDecision[] =>
      changes.map((c, i) => ({
        type: c.type,
        decision: allDiscarded
          ? "discarded"
          : decisionsRef.current[i] || "not_reviewed",
        suggestion_addressed: c.suggestion_addressed,
        description: c.description,
        original_text: c.original_text,
        anchor_text: c.anchor_text,
        placeholder: c.placeholder,
      })),
    [changes],
  );

  const completeReview = useCallback(() => {
    onReviewDecisions?.(buildDecisions());
    onComplete();
  }, [onReviewDecisions, buildDecisions, onComplete]);

  const discardReview = useCallback(() => {
    onReviewDecisions?.(buildDecisions(true));
    onCancel();
  }, [onReviewDecisions, buildDecisions, onCancel]);

  const advance = useCallback(() => {
    setInsertedRange(null);
    if (currentIndex + 1 >= changes.length) {
      restoreScroll();
      completeReview();
    } else {
      setCurrentIndex((i) => i + 1);
    }
  }, [currentIndex, changes.length, completeReview, restoreScroll]);

  const handleAccept = useCallback(() => {
    decisionsRef.current[currentIndex] = "accepted";
    if (!editor || !insertedRange) {
      advance();
      return;
    }

    const docSize = editor.state.doc.content.size;
    const from = Math.min(insertedRange.from, docSize);
    const to = Math.min(insertedRange.to, docSize);
    if (from < to) {
      // Remove the highlight via a raw removeMark transaction so we don't
      // create a non-empty selection. Same reasoning as the addMark calls in
      // applyChange — a non-empty selection here would trigger BubbleMenu →
      // tippy.show() and crash on the just-mutated DOM.
      const highlightMark = editor.schema.marks.highlight;
      if (highlightMark) {
        const tr = editor.state.tr.removeMark(from, to, highlightMark);
        editor.view.dispatch(tr);
      }
    }
    editor.commands.setTextSelection(editor.state.doc.content.size);

    advance();
  }, [editor, insertedRange, advance, currentIndex]);

  const handleReject = useCallback(() => {
    decisionsRef.current[currentIndex] = "discarded";
    if (!editor) {
      advance();
      return;
    }

    if (insertedRange) {
      const currentChange = changes[currentIndex];
      const docSize = editor.state.doc.content.size;
      const from = Math.min(insertedRange.from, docSize);
      const to = Math.min(insertedRange.to, docSize);

      if (currentChange?.type === "sentence_rewrite" && currentChange.original_text) {
        // Restore original text instead of deleting
        if (from < to) {
          editor
            .chain()
            .focus()
            .setTextSelection({ from, to })
            .deleteSelection()
            .insertContent(currentChange.original_text)
            .run();
        }
      } else if (currentChange?.type === "placeholder_fill" && currentChange.placeholder) {
        // Restore the original placeholder text
        if (from < to) {
          editor
            .chain()
            .focus()
            .setTextSelection({ from, to })
            .deleteSelection()
            .insertContent(currentChange.placeholder)
            .run();
        }
      } else {
        // content_addition — just delete the inserted range
        if (from < to) {
          editor
            .chain()
            .focus()
            .deleteRange({ from, to })
            .run();
        }
      }
    }

    advance();
  }, [editor, insertedRange, advance, changes, currentIndex]);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const hasAcceptedChanges = currentIndex > 0;

  const handleCancelClick = useCallback(() => {
    setShowCancelConfirm(true);
  }, []);

  const handleKeepChanges = useCallback(() => {
    // The currently-shown change is being removed (kept-as-is == discarded);
    // changes after it were never reviewed.
    decisionsRef.current[currentIndex] = "discarded";
    // Remove the currently highlighted (pending) change if any
    if (editor && insertedRange) {
      const docSize = editor.state.doc.content.size;
      const from = Math.min(insertedRange.from, docSize);
      const to = Math.min(insertedRange.to, docSize);
      if (from < to) {
        editor.chain().focus().deleteRange({ from, to }).run();
      }
    }
    setShowCancelConfirm(false);
    restoreScroll();
    completeReview();
  }, [editor, insertedRange, completeReview, restoreScroll, currentIndex]);

  const handleDiscardAll = useCallback(() => {
    // Restore the editor to its pre-review state — every accepted change
    // is undone, so no trace of the recording remains in the report.
    if (editor && snapshotRef.current) {
      editor.commands.setContent(snapshotRef.current);
    }
    setShowCancelConfirm(false);
    restoreScroll();
    discardReview();
  }, [editor, discardReview, restoreScroll]);

  if (changes.length === 0) return null;

  const current = changes[currentIndex];
  const isPlaceholder = current?.type === "placeholder_fill";
  const isRewrite = current?.type === "sentence_rewrite";
  // Preview the same HTML that will actually be inserted, so the paragraph
  // breaks the officer sees match the result (content_addition only).
  const previewContent =
    current?.type === "content_addition"
      ? formatReviewHtml(current?.content || "")
      : current?.content || "";

  return createPortal(
    <div
      className="fixed z-[9999] pointer-events-none"
      style={drag.wrapperStyle}
    >
      <div
        ref={dialogRef}
        data-tour="suggestions-review"
        data-review-index={currentIndex}
        className="pointer-events-auto flex flex-col max-h-[85vh] w-[520px] max-w-[90vw] bg-base-100 rounded-xl shadow-2xl border border-base-300 overflow-hidden animate-[fadeSlideUp_0.25s_ease-out]"
      >
        {/* Header — draggable */}
        <div
          className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-base-200 bg-base-200/80 cursor-grab active:cursor-grabbing select-none"
          onMouseDown={handleDragStart}
        >
          <div className="flex items-center gap-3">
            <h4 className="text-sm font-semibold text-gray-800 dark:text-base-content/80 m-0">
              {isRewrite ? "Review Rewrite" : "Review Addition"}
            </h4>
            <span className="text-xs font-medium text-base-content/40 bg-gray-100 dark:bg-base-300 px-2 py-0.5 rounded-full">
              {currentIndex + 1} of {changes.length}
            </span>
          </div>
          <button
            onClick={handleCancelClick}
            className="text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
            title="Stop reviewing"
          >
            <XMarkIcon className="size-5" />
          </button>
        </div>

        {/* Cancel confirmation */}
        {showCancelConfirm && (
          <div className="px-5 py-4 border-b border-base-200 bg-amber-50/50 dark:bg-amber-950/40">
            <p className="text-sm text-gray-700 dark:text-base-content/80 mb-3 m-0">
              {hasAcceptedChanges
                ? `You've accepted ${currentIndex} of ${changes.length} changes. What would you like to do with the remaining changes?`
                : "Are you sure you want to stop reviewing? Your pending changes will be discarded."}
            </p>
            <div className="flex items-center gap-2">
              {hasAcceptedChanges && (
                <Button
                  variant="primary"
                  size="xs"
                  onClick={handleKeepChanges}
                >
                  Keep Accepted Changes
                </Button>
              )}
              <Button
                variant="outline"
                size="xs"
                className="!border-red-200 dark:!border-red-900 !text-red-600 dark:!text-red-400 hover:!bg-red-50 dark:hover:!bg-red-950/40"
                onClick={handleDiscardAll}
              >
                {hasAcceptedChanges ? "Discard All" : "Discard"}
              </Button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="text-xs text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 ml-auto"
              >
                Continue reviewing
              </button>
            </div>
          </div>
        )}

        {/* Content + Actions — hidden when cancel confirm is showing */}
        {!showCancelConfirm && (
          <>
            <div className="px-5 py-4 flex-1 overflow-y-auto min-h-0">
              {current?.suggestion_addressed && (
                <p className="text-xs text-base-content/40 mb-2 m-0">
                  Addressing: <span className="text-base-content/60">{current.suggestion_addressed}</span>
                </p>
              )}
              {isPlaceholder && current?.placeholder && (
                <p className="text-xs text-base-content/40 mb-2 m-0">
                  Replacing: <span className="font-mono text-amber-600 dark:text-amber-400">{current.placeholder}</span>
                </p>
              )}

              <p className="text-sm font-medium text-gray-700 dark:text-base-content/80 mb-3 m-0">
                {current?.description}
              </p>

              {isRewrite && current?.original_text && (
                <div className="mb-2">
                  <p className="text-xs text-base-content/40 mb-1 m-0">Original:</p>
                  <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-lg px-4 py-3 text-sm text-gray-600 dark:text-base-content/70 leading-relaxed line-through opacity-75">
                    {current.original_text}
                  </div>
                </div>
              )}

              {isRewrite && (
                <p className="text-xs text-base-content/40 mb-1 m-0">Rewritten:</p>
              )}

              <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900 rounded-lg px-4 py-3">
                <div
                  className="text-sm text-gray-700 dark:text-base-content/80 leading-relaxed prose prose-sm max-w-none [&_p]:my-0 [&_p]:mb-3 [&_p:last-child]:mb-0"
                  dangerouslySetInnerHTML={{
                    __html: previewContent,
                  }}
                />
              </div>
            </div>

            <div className="shrink-0 flex items-center justify-end gap-2 px-5 py-3 border-t border-base-200 bg-base-200/50">
              <Button variant="outline" size="sm" onClick={handleReject} data-tour="review-discard">
                Discard
              </Button>
              <Button variant="primary" size="sm" onClick={handleAccept} data-tour="review-accept">
                Accept Changes
              </Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
